import React from 'react';
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  IconButton,
  Collapse,
  Typography,
  Box,
  Tooltip,
  Button,
} from '@material-ui/core';
import { KeyboardArrowDown, KeyboardArrowUp, Autorenew, Cached } from '@material-ui/icons';
import { getFailedJobs, reProcessOfflineJobById } from 'services/eventServices';
import { useTranslation } from 'react-i18next';
import PaginationData from 'types/models/PaginationData';
import InfoBanner from 'components/InfoBanner';
import ConfirmDialog from 'components/ConfirmDialog';
import AppContext from 'contexts/AppContext';

interface FailedJob {
  id: number;
  exception: string;
  failed_at: string;
  parsed_payload?: {
    clover_point_id: string;
    venue_id: string;
    cash_boxes_quantity: number;
    orders_quantity: number;
    refunds_quantity: number;
  };
  payload?: string;
}

export default function FailedJobsPage(): JSX.Element {
  const { t } = useTranslation();
  const { setIsLoading } = React.useContext(AppContext);
  const [openCollapseDetail, setOpenCollapseDetail] = React.useState(0);
  const [paginationFailedJobs, setPaginationFailedJobs] = React.useState<PaginationData<FailedJob> | null>(null);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [selectedFailedJob, setSelectedFailedJob] = React.useState<FailedJob | null>(null);
  const [bannerMessage, setBannerMessage] = React.useState<{
    message: string;
    result: boolean;
  } | null>(null);

  const loadData = async (page?: number) => {
    try {
      setIsLoading(true);
      const result = await getFailedJobs(page);
      setPaginationFailedJobs(result);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const reProcessOfflineJob = async () => {
    try {
      if (!selectedFailedJob) {
        throw new Error('No se ha seleccionado un job para reprocesar');
      }
      await reProcessOfflineJobById(selectedFailedJob.id);

      setSelectedFailedJob(null);
      setBannerMessage({
        message: t('failed-jobs.reprocess-sended'),
        result: true,
      });
      setTimeout(() => {
        loadData();
      }, 3000);
    } catch (error) {
      if ((error as any).response?.status === 404) {
        setBannerMessage({
          message: t('failed-jobs.reprocess-not-found'),
          result: false,
        });
        loadData();
      } else {
        setBannerMessage({
          message: t('failed-jobs.reprocess-error'),
          result: false,
        });
      }
    }
  };

  const handleCollapse = (id: number) => {
    if (openCollapseDetail === id) {
      setOpenCollapseDetail(0);
    } else {
      setOpenCollapseDetail(id);
    }
  };

  const reloadData = async () => {
    await loadData();
  };

  React.useEffect(() => {
    loadData();
  }, []);

  const renderReOpenButton = (failedJob: FailedJob): JSX.Element => {
    return (
      <Tooltip title={t('failed-jobs.reprocess').toString()}>
        <IconButton
          color="inherit"
          onClick={() => {
            setSelectedFailedJob(failedJob);
            setOpenDialog(true);
          }}
        >
          <Autorenew />
        </IconButton>
      </Tooltip>
    );
  };

  const renderContent = (failedJob: FailedJob, index: number): JSX.Element => {
    return (
      <React.Fragment key={index}>
        <TableRow className={'content-table'}>
          <TableCell>{failedJob.id}</TableCell>
          <TableCell>{failedJob.exception.slice(0, 300)}...</TableCell>
          <TableCell>
            <IconButton aria-label="expand row" size="small" onClick={() => handleCollapse(failedJob.id)}>
              {failedJob.id === openCollapseDetail ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCell>
          <TableCell>{failedJob.failed_at}</TableCell>
          <TableCell>{renderReOpenButton(failedJob)}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
            <Collapse in={failedJob.id === openCollapseDetail} timeout="auto" unmountOnExit={true}>
              <Box margin={1}>
                <Typography variant="h6" gutterBottom={true} component="div">
                  {t('detail')}
                </Typography>
                <Table size="small" aria-label="details!">
                  <TableHead className="table-header">
                    <TableRow>
                      <TableCell>{t('failed-jobs.clover-point-id')}</TableCell>
                      <TableCell>{t('failed-jobs.venue-id')}</TableCell>
                      <TableCell>{t('failed-jobs.cash-boxes-quantity')}</TableCell>
                      <TableCell>{t('failed-jobs.orders-quantity')}</TableCell>
                      <TableCell>{t('failed-jobs.refunds-quantity')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        {failedJob?.parsed_payload?.clover_point_id}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {failedJob?.parsed_payload?.venue_id}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {failedJob?.parsed_payload?.cash_boxes_quantity}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {failedJob?.parsed_payload?.orders_quantity}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {failedJob?.parsed_payload?.refunds_quantity}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  return (
    <div>
      <ConfirmDialog
        open={openDialog}
        setOpen={setOpenDialog}
        onConfirm={reProcessOfflineJob}
        onCancel={() => setOpenDialog(false)}
      />
      <InfoBanner
        message={bannerMessage?.message ?? ''}
        result={Boolean(bannerMessage?.result)}
        onClose={() => {
          setBannerMessage(null);
        }}
      />
      <Button
        variant="outlined"
        color="primary"
        onClick={() => {
          reloadData();
        }}
      >
        <Cached />
      </Button>

      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>{t('failed-jobs.error-label')}</TableCell>
              <TableCell>{t('failed-jobs.payload')}</TableCell>
              <TableCell>{t('failed-jobs.date')}</TableCell>
              <TableCell>{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginationFailedJobs?.data && paginationFailedJobs?.data.length > 0 ? (
              paginationFailedJobs?.data.map((failedJob: FailedJob, index) => renderContent(failedJob, index))
            ) : (
              <TableRow>
                <TableCell>{t('consumptionsReport-page-noOperations')}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {paginationFailedJobs && (
        <TablePagination
          component="div"
          count={paginationFailedJobs.total}
          page={paginationFailedJobs.currentPage - 1}
          onPageChange={(_, page) => loadData(page + 1)}
          rowsPerPage={paginationFailedJobs.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
          }}
        />
      )}
    </div>
  );
}
